<script setup>
import { computed, getCurrentInstance } from 'vue'

import DropdownButton from '@/components/DropdownButton.vue'
import GbButton from '@/components/GbButton.vue'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { useContentAccessStore } from '@/content_access/content_access.store.ts'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { urlHelpers } from '@/util/links.js'

const props = defineProps({
  resource: {
    type: Object,
    required: true
  },
  condenseButtons: {
    type: Boolean,
    required: false,
    default: false
  },
  showTooltips: {
    type: Boolean,
    default: false
  },
  /** Offers a signed-out viewer Sign in / Create an account, returning here, in place of the download. */
  showUnauthenticated: {
    type: Boolean,
    default: false
  }
})
const contentAccessStore = useContentAccessStore()
const { authInfo } = storeToRefs(useUserStore())
const { contentAccessStatus } = storeToRefs(contentAccessStore)

const downloadLinks = computed(() => {
  return props.resource.download_links || []
})

const googleDriveLinks = computed(() => {
  return props.resource.google_drive_links || []
})

const contentInfo = computed(() => {
  return {
    cu: props.resource.uuid,
    ct: props.resource.title,
    ctype: CONTENT_TYPES.RESOURCE
  }
})

const id = getCurrentInstance().uid

const signInUrl = computed(() => urlHelpers.signInReturn(window.location.href))
const signUpUrl = computed(() => urlHelpers.signUpReturn('toolkit', window.location.href))
const linkButtonClasses = 'inline-flex items-center justify-center rounded-xs px-4 py-2 text-text-md leading-[normal] focus-visible:outline-none focus-visible:ring-2'

const emit = defineEmits(['resourceDownloadOpened', 'resourceDownloadClicked'])

const downloadPermissions = computed(() => {
  return actionPermissions(
    ACTION_TYPES.DOWNLOAD,
    CONTENT_TYPES.RESOURCE,
    props.resource,
    authInfo.value,
    contentAccessStatus.value
  )
})

const hasDownloadAccess = computed(() => !downloadPermissions.value.block)
const resourceUuid = computed(() => props.resource.uuid)
const resourceTitle = computed(() => props.resource.title)

const analytics = getAnalyticsTracker().getChildContext({
  resource_uuid: resourceUuid,
  resource_title: resourceTitle,
  resource_type: computed(() => props.resource.type),
  resource_access: hasDownloadAccess,
  resource_download_count: computed(() => contentAccessStore.downloadCount),
  content_access_report_error: computed(() => contentAccessStatus.value.reportError),
  resource_language: computed(() => props.resource.language),
  uuid: resourceUuid,
  title: resourceTitle,
  access: hasDownloadAccess
})
const envelopeContentInfo = computed(() => ({ cu: props.resource.uuid, ct: props.resource.title }))

const validateAndOpenDownload = (label, url, isGoogleDrive) => {
  let data = {
    download_url: url,
    label: label,
    is_google_drive_link: isGoogleDrive,
    isRedownload: contentAccessStatus.value.redownloadMap[props.resource.uuid] || false,
    resource_language: props.resource.language
  }
  if (props.resource['$_source']) {
    data.source = props.resource['$_source']
  }
  if (!downloadPermissions.value.block) {
    contentAccessStore.trackDownload({ resource: props.resource, link: url })
    analytics.sendEvent(EVENT_NAMES.RESOURCES.OPENED_DOWNLOAD_LINK, { ...data, ...contentInfo.value }, envelopeContentInfo.value)
    emit('resourceDownloadOpened')
    window.open(url)
  }
}

const capitalizeLinkType = (linkType) => {
  return linkType.charAt(0).toUpperCase() + linkType.slice(1)
}

const formatLabelWithSet = (label) => {
  if (props.resource.set_number && props.resource.set_number !== 0) {
    // Insert " Set X" before the parentheses
    const parenIndex = label.lastIndexOf('(')
    if (parenIndex > 0) {
      const beforeParen = label.substring(0, parenIndex).trim()
      const afterParen = label.substring(parenIndex)
      return `${beforeParen} Set ${props.resource.set_number} ${afterParen}`
    } else {
      // No parentheses, just add to the end
      return `${label} Set ${props.resource.set_number}`
    }
  }
  return label
}

const downloadLinkItems = computed(() => {
  let items = []
  downloadLinks.value.forEach((link) => {
    const item = {
      label: 'Download ' + formatLabelWithSet(link.label),
      command: () => {
        validateAndOpenDownload(link.label, link.url, false)
      }
    }
    items.push(item)
  })
  return items
})

const googleDriveLinkItems = computed(() => {
  let items = []
  googleDriveLinks.value.forEach((link) => {
    const item = {
      label: formatLabelWithSet(link.label) + (link.link_type ? ' (' + capitalizeLinkType(link.link_type) + ')' : ''),
      command: () => {
        validateAndOpenDownload(link.label, link.url, true)
      }
    }
    items.push(item)
  })
  return items
})

const BLOCKED_TOOLTIP_OPTIONS = { disableClosingTrigger: true }

const downloadBlockedButtonText = () => {
  return (
    (downloadPermissions.value.blockExpired && 'Membership expired') ||
    (downloadPermissions.value.blockQuota && 'Download quota exceeded')
  )
}
const downloadBlockedTooltipText = () => {
  if (downloadPermissions.value.blockExpired)
    return 'To download this resource, you must have an active membership through your school or district. Contact your administrator to purchase or renew your premium membership.'
  if (downloadPermissions.value.blockQuota)
    return `You have already used all of your available downloads for this month.`
}

const contentBlocked = () => {
  contentAccessStore.sendContentAccessBlockedQuota({
    actionType: ACTION_TYPES.DOWNLOAD,
    contentInfo: contentInfo.value
  })
}

const trackDownloadsShown = (event, links) => {
  if (!event) {
    analytics.sendEvent(EVENT_NAMES.RESOURCES.CLICKED_DOWNLOAD, {
      download_links: links.map((link) => link.label + '|' + link.url),
      ...contentInfo.value
    }, envelopeContentInfo.value)
    emit('resourceDownloadClicked')
  }
}
</script>
<template>
  <div v-if="downloadLinks?.length || googleDriveLinks?.length" class="flex flex-wrap gap-2">
    <DropdownButton
      v-if="downloadLinks?.length && !downloadPermissions.block"
      :buttonAriaLabel="'Download ' + resource.title"
      :items="downloadLinkItems"
      buttonTheme="bluePrimary"
      buttonSize="small"
      :dropdownId="'download-dropdown-' + id"
      @dropdown:opened="($event) => trackDownloadsShown($event, downloadLinks)"
      :additionalClasses="condenseButtons ? '!p-1' : ''"
      :tooltipText="showTooltips ? 'Download' : ''"
    >
      <template #buttonContents>
        <span :class="['material-icons-outlined', condenseButtons ? 'text-display-sm' : 'text-lg']">file_download</span>
        <template v-if="!condenseButtons">
          Download
          <span class="material-icons-round text-lg">arrow_drop_down</span>
        </template>
      </template>
    </DropdownButton>
    <DropdownButton
      v-if="googleDriveLinks?.length && !downloadPermissions.block"
      :buttonAriaLabel="`Open with Google: ${resource.title} resource`"
      :items="googleDriveLinkItems"
      buttonTheme="blueOutlineSecondary"
      buttonSize="small"
      :dropdownId="'google-drive-dropdown-' + id"
      @dropdown:opened="($event) => trackDownloadsShown($event, googleDriveLinks)"
      :additionalClasses="condenseButtons ? '!p-1' : ''"
      :tooltipText="showTooltips ? 'Open with Google' : ''"
    >
      <template #buttonContents>
        <span :class="['material-icons-round', condenseButtons ? 'text-display-sm' : 'text-lg']">add_to_drive</span>
        <template v-if="!condenseButtons">
          Open With Google
          <span class="material-icons-round text-lg">arrow_drop_down</span>
        </template>
      </template>
    </DropdownButton>
    <template v-if="downloadPermissions.block">
      <GbButton
        v-if="!downloadPermissions.blockUnauthenticated && downloadLinks.length"
        gbTheme="bluePrimary"
        :tooltipText="downloadBlockedTooltipText()"
        :tooltip-options="BLOCKED_TOOLTIP_OPTIONS"
        @click="contentBlocked"
      >
        {{ downloadBlockedButtonText() }}
      </GbButton>
      <GbButton
        v-if="!downloadPermissions.blockUnauthenticated && googleDriveLinks.length"
        gbTheme="blueOutlineSecondary"
        :tooltipText="downloadBlockedTooltipText()"
        :tooltip-options="BLOCKED_TOOLTIP_OPTIONS"
        @click="contentBlocked"
      >
        <span class="material-icons-round pr-1 text-lg">add_to_drive</span>
        {{ downloadBlockedButtonText() }}
      </GbButton>
      <template v-if="downloadPermissions.blockUnauthenticated && showUnauthenticated">
        <a :href="signInUrl" :class="[linkButtonClasses, 'bg-primary-400 text-white hover:bg-primary-500 focus-visible:ring-primary-500']">Sign in</a>
        <a :href="signUpUrl" :class="[linkButtonClasses, 'border border-gray-400 text-gray-900 hover:border-gray-500 hover:bg-gray-300 focus-visible:ring-primary-500']">Create an account</a>
      </template>
    </template>
  </div>
</template>
